import React, { useEffect, useState } from "react";
import GlobalFeedback from "../../../global/GlobalFeedback";
import { getDonorDonationTracking } from "../api/donorPanelApi";
import DonationTrackingSummarySection from "../components/tracking/DonationTrackingSummarySection";
import DonationTrackingProgressSection from "../components/tracking/DonationTrackingProgressSection";
import DonationTrackingDonationsDirectorySection from "../components/tracking/DonationTrackingDonationsDirectorySection";
import DonationTrackingEquipmentDirectorySection from "../components/tracking/DonationTrackingEquipmentDirectorySection";
import { formatTrackingDate } from "../utils/donationTrackingFormatters";
import "../styles/DonorDonationTracking.css";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

const EMPTY_TRACKING = {
  donor: null,
  summary: {
    total_donations: 0,
    pending_donations: 0,
    approved_donations: 0,
    pending_amount: 0,
    approved_amount: 0,
    last_donation_date: null,
  },
  equipment_summary: {
    total_equipment: 0,
    total_quantity: 0,
    total_estimated_value: 0,
    verified_equipment: 0,
    pending_equipment: 0,
  },
  donations: [],
  equipment: [],
};

export default function DonorDonationTracking() {
  const [tracking, setTracking] = useState(EMPTY_TRACKING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getDonorDonationTracking();
        if (!alive) return;
        const data = res?.data || {};
        const summaryPayload = data.summary || {
          total_donations:
            Number(data.pending_donations || 0) +
            Number(data.approved_donations || 0),
          pending_donations: data.pending_donations,
          approved_donations: data.approved_donations,
        };
        setTracking({
          donor: data.donor || null,
          summary: { ...EMPTY_TRACKING.summary, ...summaryPayload },
          equipment_summary: {
            ...EMPTY_TRACKING.equipment_summary,
            ...(data.equipment_summary || {}),
          },
          donations: Array.isArray(data.donations) ? data.donations : [],
          equipment: Array.isArray(data.equipment) ? data.equipment : [],
        });
      } catch (err) {
        if (!alive) return;
        setError(err?.message || "Failed to load donation tracking");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const donorName = tracking.donor?.donor_name;

  return (
    <div style={{ width: "100%", maxWidth: "100%" }}>
      <div className="user-builder-panel">
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Donation tracking</h3>
          <p className="user-builder-panel__hint">
            Monitor verification status for your cash donations and equipment gifts.
            Pending items await organization review; approved items are confirmed in
            our records.
          </p>
          {donorName ? (
            <p className="user-builder-panel__hint" style={{ marginTop: "0.35rem" }}>
              Signed in as <strong>{donorName}</strong>
              {tracking.summary.last_donation_date ? (
                <>
                  {" "}
                  · Last gift:{" "}
                  <strong>
                    {formatTrackingDate(tracking.summary.last_donation_date)}
                  </strong>
                </>
              ) : null}
            </p>
          ) : null}
        </div>

        <GlobalFeedback type="error" text={error} />

        <DonationTrackingSummarySection
          loading={loading}
          summary={tracking.summary}
          equipmentSummary={tracking.equipment_summary}
        />

        <DonationTrackingProgressSection summary={tracking.summary} loading={loading} />
      </div>

      <DonationTrackingDonationsDirectorySection
        loading={loading}
        donations={tracking.donations}
      />

      <DonationTrackingEquipmentDirectorySection
        loading={loading}
        equipment={tracking.equipment}
        equipmentSummary={tracking.equipment_summary}
      />
    </div>
  );
}
